import { PageLoader, useMinLoading } from "@/components/Preloader";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Landmark, SearchIcon } from "lucide-react";
import api from "@/lib/api";
import { Regulation } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const IMPACT_COLORS: Record<string, string> = {
  critical: "bg-red-500/15 text-red-400 border-red-500/30",
  high: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  low: "bg-green-500/15 text-green-400 border-green-500/30",
};

const ALL = "__all__";

export default function RegulationTracker() {
  const [sp] = useSearchParams();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState(sp.get("country") || ALL);
  const [agency, setAgency] = useState(sp.get("agency") || ALL);
  const [status, setStatus] = useState(sp.get("status") || ALL);
  const [impact, setImpact] = useState(sp.get("impact") || ALL);

  const { data, isLoading } = useQuery({
    queryKey: ["regulations", q, country, agency, status, impact],
    queryFn: async () => {
      const params: any = {};
      if (q) params.q = q;
      if (country !== ALL) params.country = country;
      if (agency !== ALL) params.agency = agency;
      if (status !== ALL) params.status = status;
      if (impact !== ALL) params.impact = impact;
      return (await api.get("/regulations", { params })).data;
    },
  });

  const filters = data?.filters || { countries: [], agencies: [], statuses: [], impacts: [] };
  const selectCls = "bg-[#1A1A1A] border-white/10 text-white w-full sm:w-[160px]";
  const showLoader = useMinLoading(isLoading);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="regulation-tracker-page">
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Landmark className="h-7 w-7 text-[#7C3AED]" />
          <h1 className="font-heading font-black text-white text-3xl sm:text-5xl">Regulation Tracker</h1>
        </div>
        <p className="text-gray-400 max-w-2xl">
          Searchable intelligence on crypto policy — filter by jurisdiction, agency, status and impact.
        </p>
      </header>

      <div className="flex flex-col sm:flex-row gap-3 mb-8 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search regulations..."
            data-testid="regulation-search-input"
            className="pl-9 bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500" />
        </div>
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className={selectCls} data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white">
            <SelectItem value={ALL}>All countries</SelectItem>
            {filters.countries.map((c: string) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={agency} onValueChange={setAgency}>
          <SelectTrigger className={selectCls} data-testid="filter-agency"><SelectValue placeholder="Agency" /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white">
            <SelectItem value={ALL}>All agencies</SelectItem>
            {filters.agencies.map((a: string) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className={selectCls} data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white">
            <SelectItem value={ALL}>All statuses</SelectItem>
            {filters.statuses.map((s: string) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={impact} onValueChange={setImpact}>
          <SelectTrigger className={selectCls} data-testid="filter-impact"><SelectValue placeholder="Impact" /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white">
            <SelectItem value={ALL}>All impact levels</SelectItem>
            {filters.impacts.map((i: string) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {showLoader ? (
        <PageLoader />
      ) : (data?.items || []).length === 0 ? (
        <p className="text-gray-500 py-16 text-center">No regulation entries match those filters.</p>
      ) : (
        <div className="space-y-4">
          {data.items.map((r: Regulation) => (
            <Link key={r.id} to={`/regulation/${r.slug}`} data-testid={`regulation-row-${r.slug}`}
              className="group block bg-[#121212] border border-white/10 rounded-lg p-5 sm:p-6 hover:border-[#7C3AED]/50 transition-all duration-200 hover:-translate-y-0.5">
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="outline" className={IMPACT_COLORS[r.impact_level] || IMPACT_COLORS.medium}>
                  {r.impact_level} impact
                </Badge>
                <Badge variant="outline" className="border-white/20 text-gray-300 font-mono text-[10px] uppercase">
                  {r.status.replace("_", " ")}
                </Badge>
                {r.effective_date && (
                  <Badge variant="outline" className="border-white/10 text-gray-500 font-mono text-[10px]">
                    Effective {r.effective_date}
                  </Badge>
                )}
              </div>
              <h3 className="font-heading font-semibold text-white text-lg leading-snug group-hover:text-[#7C3AED] transition-colors">
                {r.title}
              </h3>
              <p className="text-gray-400 text-sm mt-2 line-clamp-2">{r.summary}</p>
              <p className="font-mono text-[10px] text-gray-500 mt-3 uppercase tracking-wide">
                {r.countries.join(", ")} {r.agencies.length > 0 && `· ${r.agencies.join(", ")}`}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
